import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import {
  industryOptions,
  categories,
  stateOptions,
  stages,
  topicOptions,
} from "../constants/formOptions";
import "./users.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const UserTable = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [industry, setIndustry] = useState("");
  const [category, setCategory] = useState("");
  const [stateLocation, setStateLocation] = useState("");
  const [country, setCountry] = useState("");
  const [stage, setStage] = useState("");
  const [topic, setTopic] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/admin/users`)
      .then((response) => setUsers(response.data))
      .catch((error) => console.error("Error fetching users:", error));
  }, []);

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.mobile?.toLowerCase().includes(term);

    const matchField = (filter, value) =>
      !filter ||
      filter.toLowerCase() === "all" ||
      value?.toLowerCase().includes(filter.toLowerCase());

    return (
      matchesSearch &&
      matchField(industry, user.industry) &&
      matchField(category, user.area) &&
      matchField(stateLocation, user.state) &&
      matchField(country, user.country) &&
      matchField(stage, user.stage) &&
      matchField(topic, user.topic)
    );
  });

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredUsers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "users_data.xlsx");
  };
  return (
    <div className="p-4 users-page">
      <h2 className="admin-heading">Admin Users</h2>

      {/* Filter Section */}
      <div className="filter-section">
        <h3 className="filter-heading">Filter By</h3>
        <div className="filter-bar">
          <div className="filter-group">
            <label className="filter-label">Industry</label>
            <input
              list="industry-options"
              placeholder="Select Industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="filter-input"
            />
            <datalist id="industry-options">
              {industryOptions.map((opt, idx) => (
                <option key={idx} value={opt} />
              ))}
            </datalist>
          </div>

          <div className="filter-group">
            <label className="filter-label">Category</label>
            <input
              list="category-options"
              placeholder="Select Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="filter-input"
            />
            <datalist id="category-options">
              {categories.map((opt, idx) => (
                <option key={idx} value={opt} />
              ))}
            </datalist>
          </div>

          <div className="filter-group">
            <label className="filter-label">State</label>
            <input
              list="state-options"
              placeholder="Select State"
              value={stateLocation}
              onChange={(e) => setStateLocation(e.target.value)}
              className="filter-input"
            />
            <datalist id="state-options">
              {stateOptions.map((opt, idx) => (
                <option key={idx} value={opt} />
              ))}
            </datalist>
          </div>

          <div className="filter-group">
            <label className="filter-label">Country</label>
            <input
              list="country-options"
              placeholder="Select Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="filter-input"
            />
            <datalist id="country-options">
              {["India", "USA", "UK", "Australia", "Canada"].map((opt, idx) => (
                <option key={idx} value={opt} />
              ))}
            </datalist>
          </div>

          <div className="filter-group">
            <label className="filter-label">Stage</label>
            <input
              list="stage-options"
              placeholder="Select Stage"
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              className="filter-input"
            />
            <datalist id="stage-options">
              {stages.map((opt, idx) => (
                <option key={idx} value={opt} />
              ))}
            </datalist>
          </div>

          <div className="filter-group">
            <label className="filter-label">Topic</label>
            <input
              list="topic-options"
              placeholder="Select Topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="filter-input"
            />
            <datalist id="topic-options">
              {topicOptions.map((opt, idx) => (
                <option key={idx} value={opt} />
              ))}
            </datalist>
          </div>
        </div>
      </div>

      {/* Search Bar Below Filters */}
      <div className="search-wrapper">
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input
            type="text"
            placeholder="Name, Email, or Mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>
      </div>

      <div className="export-container" style={{ margin: "20px 0" }}>
        <button onClick={exportToExcel} className="export-button">
          Export to Excel
        </button>
      </div>

      {/* User Table */}
      <div class="table-wrapper">
        <div className="overflow-x-auto rounded-2xl shadow-md border border-gray-200 mt-6">
          <table className="min-w-[1000px] w-full text-sm text-left text-gray-700">
            <thead className="bg-gray-100 text-xs text-gray-600 uppercase tracking-wider sticky top-0 z-10">
              <tr>
                {[
                  "ID",
                  "Name",
                  "Email",
                  "Mobile",
                  "Problem",
                  "Topic",
                  "Industry",
                  "Area",
                  "Stage",
                  "Country",
                  "State",
                  "Business Description",
                  "Goal",
                  "Target Audience",
                  "Team Size",
                  "Revenue Range",
                  "Registration Type",
                  "Biggest Challenge",
                  "What Tried",
                  "Support Area",
                  "Used AI Tools",
                  "Website",
                  "LinkedIn",
                  "Facebook",
                  "Instagram",
                  "Twitter",
                  "YouTube",
                  "Business Phone",
                  "GST Number",
                  "Consent to Use",
                  "Want Review",
                  "Opt-in For",
                  "Business Email",
                  "Credits",
                  "Tokens Used",
                  "Activity Type",
                  "Response",
                  "Timestamp",
                  "Last Chat",
                ].map((col) => (
                  <th key={col} className="px-4 py-2 border">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 border-b">
                  <td className="px-4 py-2">{user.id}</td>
                  <td className="px-4 py-2">{user.name}</td>
                  <td className="px-4 py-2">{user.email}</td>
                  <td className="px-4 py-2">{user.mobile}</td>
                  <td className="px-4 py-2">{user.problem}</td>
                  <td className="px-4 py-2">{user.topic}</td>
                  <td className="px-4 py-2">{user.industry}</td>
                  <td className="px-4 py-2">{user.area}</td>
                  <td className="px-4 py-2">{user.stage}</td>
                  <td className="px-4 py-2">{user.country}</td>
                  <td className="px-4 py-2">{user.state}</td>
                  <td className="px-4 py-2">{user.business_description}</td>
                  <td className="px-4 py-2">{user.goal}</td>
                  <td className="px-4 py-2">{user.target_audience}</td>
                  <td className="px-4 py-2">{user.team_size}</td>
                  <td className="px-4 py-2">{user.revenue_range}</td>
                  <td className="px-4 py-2">{user.registration_type}</td>
                  <td className="px-4 py-2">{user.biggest_challenge}</td>
                  <td className="px-4 py-2">{user.what_tried}</td>
                  <td className="px-4 py-2">{user.support_area}</td>
                  <td className="px-4 py-2">{user.used_ai_tools}</td>
                  <td className="px-4 py-2">{user.website}</td>
                  <td className="px-4 py-2">{user.linkedin}</td>
                  <td className="px-4 py-2">{user.facebook}</td>
                  <td className="px-4 py-2">{user.instagram}</td>
                  <td className="px-4 py-2">{user.twitter}</td>
                  <td className="px-4 py-2">{user.youtube}</td>
                  <td className="px-4 py-2">{user.business_phone}</td>
                  <td className="px-4 py-2">{user.gst_number}</td>
                  <td className="px-4 py-2">
                    {user.consent_to_use ? "Yes" : "No"}
                  </td>
                  <td className="px-4 py-2">
                    {user.want_review ? "Yes" : "No"}
                  </td>
                  <td className="px-4 py-2">{user.opt_in_for}</td>
                  <td className="px-4 py-2">{user.business_email}</td>
                  <td className="px-4 py-2">{user.credits}</td>
                  <td className="px-4 py-2">{user.tokens_used}</td>
                  <td className="px-4 py-2">{user.activity_type}</td>
                  <td className="px-4 py-2">{user.response}</td>
                  <td className="px-4 py-2">{user.timestamp}</td>
                  <td className="px-4 py-2">{user.last_chat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserTable;
